import React, { type ReactNode, useEffect, useState } from 'react'

type TutorialImageProps = {
  align?: 'center' | 'left'
  src: string
  alt: string
  width: number
  height: number
  maxHeight?: number
  fillWidth?: boolean
  children?: ReactNode
}

export function TutorialImage({
  align = 'center',
  src,
  alt,
  width,
  height,
  maxHeight = height,
  fillWidth = false,
  children,
}: TutorialImageProps) {
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [hasImageError, setHasImageError] = useState(false)

  const handleImageLoad = () => {
    setIsImageLoaded(true)
  }

  const handleImageError = () => {
    setHasImageError(true)
  }

  useEffect(() => {
    let isActive = true
    const image = new Image()

    setIsImageLoaded(false)
    setHasImageError(false)

    image.onload = () => {
      if (isActive) setIsImageLoaded(true)
    }
    image.onerror = () => {
      if (isActive) setHasImageError(true)
    }
    image.src = src

    if (image.complete) setIsImageLoaded(true)

    return () => {
      isActive = false
    }
  }, [src])

  return (
    <div
      className={`relative flex w-full items-center justify-center overflow-hidden rounded-md bg-[#f4f4f5] ${
        align === 'left' ? 'mr-auto' : 'mx-auto'
      }`}
      style={{
        aspectRatio: `${width} / ${height}`,
        maxHeight: fillWidth ? undefined : maxHeight,
        maxWidth: fillWidth ? undefined : (maxHeight * width) / height,
      }}
    >
      {!isImageLoaded && !hasImageError && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-[#f4f4f5] via-[#ececef] to-[#f4f4f5]" />
      )}

      {hasImageError ? (
        <div className="px-6 text-center text-sm text-[#6b7280]">
          Failed to load tutorial image.
        </div>
      ) : (
        <img
          className={`block h-full w-full object-contain transition-opacity duration-200 ${
            isImageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          src={src}
          width={width}
          height={height}
          loading="lazy"
          decoding="async"
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      )}

      {!hasImageError && children}
    </div>
  )
}
